<template>
  <main v-if="post" :data-wio-id="postId" class="post">
    <div class="header">
      <img
        :src="post.featured_image.url"
        :class="post.image_bg_color ? post.image_bg_color : 'chequer'"
        class="header__image"
      />
    </div>

    <div class="share-wrapper">
      <div class="share-bar">
        <router-link to="/reports">
          &larr; {{ $t('common.btnBack') }}
        </router-link>
        <span class="share-bar__info">
          {{ post.release | formatDate }}
        </span>
      </div>
    </div>

    <section class="content">
      <h1>{{ post.title[0].text }}</h1>
      <RichText v-if="post.post" :field="post.post" class="rich-text" />

      <template v-if="post.cta_button && post.cta_target">
        <router-link
          v-if="post.cta_target.startsWith('/')"
          :to="post.cta_target"
          class="cta-link"
        >
          <Button class="btn-primary">{{ post.cta_button }}</Button>
        </router-link>
        <a v-else :href="post.cta_target" class="cta-link" target="_blank">
          <Button class="btn-primary">{{ post.cta_button }}</Button>
        </a>
      </template>

      <div v-if="postBlob" class="post__footer">
        <router-link to="/">
          <img
            :src="postBlob.primary.separator.url"
            :alt="postBlob.primary.separator.alt"
          />
        </router-link>
        <RichText :field="postBlob.primary.blob" class="rich-text" />
      </div>
    </section>
  </main>
</template>
<script>
import dayjs from 'dayjs';
import { mapState } from 'vuex';
import Button from '@/components/Button';
import Icon from '@/components/Icon';

export default {
  components: {
    Button,
    Icon
  },
  metaInfo() {
    return {
      title: this.post ? this.post.page_title : 'Meqyas',
      meta: [
        {
          name: 'og:image',
          content: this.post ? this.post.share_image.url : '',
          vmid: 'og:image'
        },
        {
          name: 'og:description',
          content: this.post ? this.post.short[0].text : '',
          vmid: 'og:description'
        },
        {
          name: 'twitter:description',
          content: this.post ? this.post.short[0].text : '',
          vmid: 'twitter:description'
        },
        {
          name: 'twitter:image',
          content: this.post ? this.post.featured_image.url : '',
          vmid: 'twitter:image'
        }
      ]
    };
  },
  filters: {
    formatDate(date) {
      return dayjs(date).format('MMMM YYYY');
    }
  },
  computed: {
    ...mapState(['reports', 'pages']),
    post() {
      if (!this.reports) {
        return;
      }

      const page = this.reports.find(({ data }) =>
        this.$route.path.includes(data.path)
      );

      return page ? page.data : undefined;
    },
    postId() {
      if (!this.reports) {
        return;
      }

      const page = this.reports.find(({ data }) =>
        this.$route.path.includes(data.path)
      );

      return page ? page.id : undefined;
    },
    sortedPosts() {
      return this.reports
        .slice()
        .sort((a, b) => new Date(b.data.release) - new Date(a.data.release))
        .map(post => post.data);
    },
    nextPost() {
      const currentPostIndex = this.sortedPosts.indexOf(this.post);
      return this.sortedPosts[currentPostIndex - 1];
    },
    prevPost() {
      const currentPostIndex = this.sortedPosts.indexOf(this.post);
      return this.sortedPosts[currentPostIndex + 1];
    },
    postBlob() {
      if (!this.pages) {
        return;
      }

      const page = this.pages.find(({ data }) =>
        this.$route.path.includes(data.path)
      );

      const blob = page
        ? page.data.body.find(
            ({ slice_type }) => slice_type === 'blog_post_blob'
          )
        : undefined;

      return blob;
    }
  },
  watch: {
    pages(newPages, oldPages) {
      // Refetch pages when language changed
      if (oldPages.length && !newPages.length) {
        this.$store.dispatch('getPages');
      }
    },
    reports(newReports, oldReports) {
      // Refetch reports when language changed
      if (oldReports.length && !newReports.length) {
        this.$store.dispatch('getReports');
      }
    }
  },
  serverPrefetch() {
    return Promise.all([
      this.$store.dispatch('getReports'),
      this.$store.dispatch('getPages')
    ]);
  },
  mounted() {
    if (!this.reports.length) {
      this.$store.dispatch('getReports');
    }
    if (!this.pages.length) {
      this.$store.dispatch('getPages');
    }
    if (window && window.PrismicToolbar) {
      window.PrismicToolbar.setupEditButton();
    }
  }
};
</script>
<style lang="scss" scoped>
@import '../../scss/base/colors';
@import '../../scss/helpers/mixins';
@import '../../scss/base/fonts';

.header {
  max-height: 700px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
}
.header__image {
  width: 100%;
  max-height: 50vh;
  background-size: 150px;
  object-fit: contain;

  &.chequer {
    background-image: url('/img/tile-hatch.png');
  }
  &.beige {
    background-color: $beige;
  }
  &.light-grey {
    background-color: $grey-3;
  }
  &.grey {
    background-color: $grey-2;
  }
  &.dark-grey {
    background-color: $grey-1;
  }
  &.red {
    background-color: $red;
  }
  &.orange {
    background-color: $orange;
  }
  &.brown {
    background-color: $brown;
  }
  &.yellow {
    background-color: $yellow;
  }
  &.green {
    background-color: $green;
  }
  &.dark-green {
    background-color: $dark-green;
  }
  &.light-blue {
    background-color: $light-blue;
  }
  &.blue {
    background-color: $blue;
  }
  &.dark-blue {
    background-color: $dark-blue;
  }
  &.pink {
    background-color: $pink;
  }
  &.magenta {
    background-color: $magenta;
  }
  &.purple {
    background-color: $purple;
  }
  &.black {
    background-color: $black;
  }
  &.turquoise {
    background-color: $turquoise;
  }
  &.dark-turquoise {
    background-color: $dark-turquoise;
  }
}
.content {
  padding: 20px;

  @include breakpoint(S) {
    margin: 30px 0;
  }
  @include breakpoint(M) {
    margin: 40px 0;
  }
  @include breakpoint(L) {
    margin: 50px 0;
  }
  @include breakpoint(XL) {
    margin: 60px 0;
  }

  h1 {
    text-align: center;
    margin: 0 auto 50px;
    padding: 0 30px;
    max-width: calc(23 * 60px);
  }
  .rich-text {
    padding: 0 30px;
  }
  ::v-deep .rich-text {
    p,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    ol,
    ul,
    div,
    pre {
      max-width: 900px;
      margin-left: auto;
      margin-right: auto;
    }
    div {
      display: flex;
      justify-content: center;
    }
    li {
      line-height: 40px;
    }
    p {
      color: $grey-2;
      font-size: 16px;
      @include breakpoint(S) {
        font-size: 20px;
      }
    }
    .block-img {
      display: flex;
      justify-content: center;
      max-width: calc(15 * 60px);
      margin: 60px auto;
      img {
        display: block;
        width: 100%;
        height: 100%;
        max-height: calc(7.5 * 60px);
        object-fit: contain;
      }
    }
    h6 {
      display: flex;
      justify-content: center;
      margin: -50px auto 50px;
      font-size: 14px;
      font-weight: 400;
      color: $grey-2;
    }
    pre {
      background: none;
      border: 0;
      border-left: 5px solid $orange;
      border-radius: 0;
      color: $dark-blue;
      font-size: 36px;
      padding: 0 0 0 1.5em;
      margin: 1em auto;
      white-space: normal;
      @include setTypography;
    }
  }
  .post__footer {
    max-width: calc(15 * 60px);
    margin: 0 auto;
    padding: 0 30px;
    text-align: center;
    img {
      width: 100%;
      object-fit: contain;
    }
    .rich-text {
      text-align: left;
    }
    @include breakpoint(M) {
      padding: 0;
    }
  }
}

.share-wrapper {
  border-bottom: 1px solid $grey-3;
  margin-bottom: 30px;
  .share-bar {
    padding: 0 15px;
    height: 60px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .links {
      color: $dark-blue;
      display: flex;
      align-items: center;
      a {
        display: flex;
        align-items: center;
        margin-left: 10px;
        font-size: 1.5em;
      }
    }
    span {
      display: none;
      color: $grey-2;
      font-size: 0.9em;
    }
  }
  @include breakpoint(XS) {
    .share-bar {
      span {
        display: block;
      }
    }
  }
  @include breakpoint(M) {
    .share-bar {
      padding: 0;
      max-width: calc(23 * 40px);
      margin: 0 auto;
    }
  }
  @include breakpoint(L) {
    .share-bar {
      max-width: calc(23 * 50px);
    }
  }
  @include breakpoint(XL) {
    .share-bar {
      max-width: calc(23 * 60px);
    }
  }
}
.cta-link {
  display: block;
  text-decoration: none;
  max-width: calc(5 * 60px);
  margin: 60px auto;
}
</style>
